import React from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { PropTypes } from 'prop-types';
import withStyles from '@mui/styles/withStyles';

import {
  StoreUtil,
} from 'doctivity-shared/utils';
import {
  TableUtil,
  withRouter,
} from 'utils';
import {
  DynamicTable,
  LoadingView,
  SmallIconTextButton,
} from 'components';

import {
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';

import {
  queryTagNamespaces,
  upsertTagNamespace,
} from 'store/actions/tagNamespacesActions';

import { TagNamespaceEditDialog } from './TagNamespaceEditDialog';

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  spacer: {
    flex: 1,
  },
  leftIcon: {
    marginRight: theme.spacing(1),
  },
  switchLabel: {
    fontSize: 12,
    fontWeight: 200,
  }
});

// this controls which columns are displayed and how they are looked up in data
const columns = [
  {
    label: 'Name',
    key: 'name',
  },
  {
    label: 'Client',
    key: 'client.name',
  },
  {
    label: 'Activity Notes',
    key: 'activity_notes_drop_down',
    format: TableUtil.boolToCheckIcon,
    style: { width: 150 },
  },
  {
    label: 'Providers',
    key: 'providers_drop_down',
    format: TableUtil.boolToCheckIcon,
  },
  {
    label: 'Organizations',
    key: 'organizations_drop_down',
    format: TableUtil.boolToCheckIcon,
  },
];

class TagNamespacesPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      query: {
        order: [
          ['name', 'ASC'],
        ],
        include: [
          {
            attributes: ['id', 'name'],
            association: 'client',
          },
        ],
        limit: 50,
        offset: 0,
      },
      filterClient: false,
      addDialogOpen: false,
    };
  }

  componentDidMount() {
    if (StoreUtil.needsLoadNoCache(this.props.tagNamespaces)) {
      this.fetchData();
    }
  }

  componentDidUpdate(prevProps) {
    // is object getting edited?
    if (this.props.editing !== prevProps.editing) {
      // if so and it is newer than the list, update list
      if (StoreUtil.hasSavedSinceLoad(this.props.editing, this.props.tagNamespaces)) {
        this.fetchData();
      }
    }
  }

  fetchData() {
    const {
      query,
    } = this.state;

    if (this.state.filterClient) {
      if (!query.where) {
        query.where = { };
      }
      query.where.client_id = this.props.clientId;
    } else {
      if (query.where && query.where.client_id) {
        delete query.where.client_id;
      }
    }
    this.props.dispatch(queryTagNamespaces(query));
  }

  render() {
    const {
      user,
      tagNamespaces,
      editing,
      classes,
    } = this.props;

    const {
      query,
    } = this.state;

    // is a user being edited now?
    if (StoreUtil.isSaving(editing)) {
      return (<LoadingView />);
    }

    return (
      <>
        <Helmet defer={false}>
          <title>Labels</title>
        </Helmet>
        <div className={classes.container}>
          <DynamicTable
            header={
              <div className={classes.headerContainer}>
                <Typography
                  component='h2'
                  variant='h6'
                  color='primary'
                  gutterBottom
                >
                  Label Types
                </Typography>
                <div className={classes.spacer} />
                {user.is_admin && (
                  <FormControlLabel
                    control={<Switch checked={this.state.filterClient} onChange={this.onFilterClient} />}
                    label={<Typography className={classes.switchLabel}>Filter by Selected Client</Typography>}
                  />
                )}
                <div className={classes.spacer} />
                {user.is_admin && (
                  <SmallIconTextButton
                    onClick={this.onOpenAddDialog}
                    icon='add'
                    text='Add Label Type'
                  />
                )}
              </div>
            }
            columns={columns}
            query={query}
            data={tagNamespaces}
            onQueryChange={this.onQueryChange}
            onRowClick={(row) => {
              if (row.id) {
                this.props.router.navigate(`/admin/labels/${row.id}`);
              }
            }}
          />
          <TagNamespaceEditDialog
            open={this.state.addDialogOpen}
            onSave={this.onSaveTagNamespace}
            onClose={this.onCloseAddDialog}
          />
        </div>
      </>
    );
  }

  onQueryChange = (query) => {
    this.setState({ query }, this.fetchData);
  };

  onOpenAddDialog = () => {
    this.setState({
      addDialogOpen: true,
    });
  };

  onCloseAddDialog = () => {
    this.setState({
      addDialogOpen: false,
    });
  };

  onSaveTagNamespace = (tagNamespace) => {
    tagNamespace.user_id = this.props.user.id;
    this.props.dispatch(upsertTagNamespace(tagNamespace));
    this.onCloseAddDialog();
  };

  onFilterClient = (event) => {
    this.setState({
      filterClient: event.target.checked,
    }, this.fetchData);
  };
}

TagNamespacesPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
  user: PropTypes.object,
  tagNamespaces: PropTypes.object.isRequired,
  editing: PropTypes.object,
  clientId: PropTypes.number,
};

function mapStateToProps(state) {
   const {
    app
  } = state;

 return {
    user: state.user,
    clientId: app.selectedClient,
    tagNamespaces: StoreUtil.get(state.tagNamespaces, StoreUtil.COMMON_TABLE),
    editing: StoreUtil.get(state.tagNamespaces, StoreUtil.COMMON_EDIT_ITEM),
  };
}

const styled = withStyles(styles)(TagNamespacesPage);
const connected = connect(mapStateToProps)(styled);
const routed = withRouter(connected);
export { routed as TagNamespacesPage };
